import { ReactNode, useState } from 'react';
import { Box, Container, IconButton, List, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import { useRouteMatch } from 'react-router-dom';
import { Menu } from '@mui/icons-material';
import { PathParams, Paths } from '../../../../app/router/app.router';
import { useNavigationBarStyles } from './navigation-bar.styles';
import { UserToggle } from '../user-toggle/user-toggle.component';
import { BuildingSelectDropdown, BuildingSelectDropdownProps } from '../building-select/building-select.component';
import { NavMenuDesktopItem } from './components/nav-menu-desktop-item/nav-menu-desktop-item.component';
import { NavMenuMobileItem } from './components/nav-menu-mobile-item/nav-menu-mobile-item.component';
import { NavMenuCollapsibleMobileItem } from './components/nav-menu-collapsible-mobile-item/nav-menu-collapsible-mobile-item.component';
import { NavMenuCollapsibleDesktopItem } from './components/nav-menu-collapsible-desktop-item/nav-menu-collapsible-desktop-item.component';
import { CheckMyPermissionDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { SVGIconComponent } from '../icons/types';
import { useEnvironment } from '../../../../core/env/environment.context';
import { EnvironmentType } from '../../../../core/env/environment-type.enum';
import { ProjectSummaryToggle } from '../project-summary/project-summary-toggle.component';
import { SearchToggle } from '../search/search-toggle.component';
import { useProjectSummaryContext } from '../../../../app/layout/project-summary.context';
import { useSearchContext } from '../../../../app/layout/search.context';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';

interface BaseNavigationItem {
  text: string;
  url?: string;
  permissions?: CheckMyPermissionDto[];
  availableUiFeatures?: string[];
  icon?: SVGIconComponent;
}

/** A child with children of its own opens a nested menu: a flyout on desktop, an indented list on mobile. */
export interface NavigationItem extends BaseNavigationItem {
  children?: NavigationItem[];
}

export interface NavigationBarProps {
  items: NavigationItem[];
  user?: {
    username: string;
    onLogout(): void;
  };
  iconClick(): void;
  buildingSelect?: BuildingSelectDropdownProps;
}

export const NavigationBar = ({ items, user, buildingSelect, iconClick }: NavigationBarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  // TO DO: Remove this piece of business logic
  // Hide the bottom border on mobile view of the order details page
  // This could be extracted out as separate logic in future if we need more
  // use cases, but for now can sit here
  const match = useRouteMatch<PathParams<Paths.order> | PathParams<Paths.delivery>>([Paths.order, Paths.delivery]);
  const hideBottomBorder = !!match && isSm;
  const projectSummaryContext = useProjectSummaryContext();
  const searchContext = useSearchContext();

  return (
    <NavigationBarView
      items={items}
      user={user}
      isMobileMenuOpen={isMobileMenuOpen}
      onToggleMobileMenu={setIsMobileMenuOpen}
      isProjectSummaryOpen={projectSummaryContext.isOpen}
      onToggleProjectSummary={projectSummaryContext.onToggle}
      isSearchOpen={searchContext.isOpen}
      onToggleSearch={searchContext.onToggle}
      iconClick={iconClick}
      hideBottomBorder={hideBottomBorder}
    >
      {buildingSelect ? <BuildingSelectDropdown {...buildingSelect} /> : null}
    </NavigationBarView>
  );
};

/**
 * This dumb component is exported only for use in Storybook in order to extract the BuildingSelect component
 * which won't render in Storybook by default. The NavigationBar component above is imported in the Layout
 */
interface NavigationBarViewProps {
  isMobileMenuOpen: boolean;
  onToggleMobileMenu: (open: boolean) => void;
  isProjectSummaryOpen: boolean;
  onToggleProjectSummary: (open: boolean) => void;
  isSearchOpen: boolean;
  onToggleSearch: (open: boolean) => void;
  iconClick(): void;
  items: NavigationItem[];
  hideBottomBorder: boolean;
  user?: {
    username: string;
    onLogout(): void;
  };
  children: ReactNode;
}

export function NavigationBarView({
  isMobileMenuOpen,
  onToggleMobileMenu,
  isProjectSummaryOpen,
  onToggleProjectSummary,
  isSearchOpen,
  onToggleSearch,
  iconClick,
  items,
  hideBottomBorder,
  user,
  children
}: NavigationBarViewProps) {
  const classes = useNavigationBarStyles({ hideBottomBorder });

  return (
    <Box className={classes.root}>
      <MobileMenuItemDrawer items={items} isMobileMenuOpen={isMobileMenuOpen} onToggleMobileMenu={onToggleMobileMenu} />

      <Container className={classes.container}>
        <Box className={classes.lhs}>
          <MobileMenuIcon onToggleMobileMenu={onToggleMobileMenu} />
          <BrandIcon click={iconClick} />

          <DesktopMenuItems items={items} />
        </Box>

        <Box className={classes.rhs}>
          <Box className={classes.projectSelector}>{children}</Box>

          <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
            <SearchToggle isOpen={isSearchOpen} onToggle={onToggleSearch} />
          </PermissionBoundary>

          <Box className={classes.center}>{user ? <UserToggle {...user} /> : null}</Box>
          <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
            <ProjectSummaryToggle isOpen={isProjectSummaryOpen} onToggle={onToggleProjectSummary} />
          </PermissionBoundary>
        </Box>
      </Container>
    </Box>
  );
}

function MobileMenuIcon({ onToggleMobileMenu }: Pick<NavigationBarViewProps, 'onToggleMobileMenu'>) {
  const classes = useNavigationBarStyles({});
  return (
    <IconButton
      onClick={() => onToggleMobileMenu(true)}
      className={classes.menuButton}
      aria-label="menu"
      size={'medium'}
    >
      <Menu />
    </IconButton>
  );
}

function BrandIcon({ click }: { click(): void }) {
  const environment = useEnvironment();
  const classes = useNavigationBarStyles({});
  const logoFileName = environment.type === EnvironmentType.Production ? 'logo' : 'logo-test';
  return (
    <Box className={classes.center}>
      <img
        className={classes.logo}
        onClick={() => click()}
        src={`/media/brands/${environment.brandId}/${logoFileName}.png`}
        alt={environment.title}
      />
    </Box>
  );
}

function MobileMenuItemDrawer({
  isMobileMenuOpen: open,
  onToggleMobileMenu: setOpen,
  items
}: Pick<NavigationBarViewProps, 'isMobileMenuOpen' | 'onToggleMobileMenu' | 'items'>) {
  const classes = useNavigationBarStyles({});
  return (
    <SwipeableDrawer
      anchor={'left'}
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      onOpen={() => {}}
    >
      <List className={classes.menuItemsMobile}>
        {items.map((item) => {
          return Array.isArray(item.children) ? (
            <NavMenuCollapsibleMobileItem key={item.text} item={item} onClick={() => setOpen(false)} />
          ) : (
            <NavMenuMobileItem key={item.text} item={item} onClick={() => setOpen(false)} />
          );
        })}
      </List>
    </SwipeableDrawer>
  );
}

function DesktopMenuItems({ items }: Pick<NavigationBarViewProps, 'items'>) {
  const classes = useNavigationBarStyles({});
  return (
    <Box className={classes.menuItemsDesktop}>
      {items.map((item) =>
        Array.isArray(item.children) ? (
          <NavMenuCollapsibleDesktopItem key={item.text} item={item} />
        ) : (
          <NavMenuDesktopItem key={item.text} item={item} />
        )
      )}
    </Box>
  );
}
